import type { CoreOptions } from '@adyen/adyen-web/dist/types/core/types';
import { useEffect, useMemo, useState } from 'react';
import type DropinElement from '@adyen/adyen-web/dist/types/components/Dropin/Dropin';
import { useLocation, useNavigate } from 'react-router';
import type { AdyenPaymentSession, PaymentMethod } from '@jwp/ott-common/types/checkout';
import { getModule } from '@jwp/ott-common/src/modules/container';
import AccountController from '@jwp/ott-common/src/controllers/AccountController';
import CheckoutController from '@jwp/ott-common/src/controllers/CheckoutController';
import { modalURLFromLocation, modalURLFromWindowLocation } from '@jwp/ott-ui-react/src/utils/location';
import useQueryParam from '@jwp/ott-ui-react/src/hooks/useQueryParam';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';
import { useTranslation } from 'react-i18next';

import Adyen from '../../components/Adyen/Adyen';
import { useAriaAnnouncer } from '../AnnouncementProvider/AnnoucementProvider';

type Props = {
  setProcessing: (loading: boolean) => void;
  setPaymentError: (errorMessage?: string) => void;
  selectedPaymentMethod: PaymentMethod | undefined;
  onPayPalSubmit: () => void;
  onPaymentMethodChange: (paymentMethod: string) => void;
  paymentMethods: PaymentMethod[];
  error?: string;
};

export default function AdyenPaymentDetails({
  setProcessing,
  setPaymentError,
  error,
  selectedPaymentMethod,
  onPayPalSubmit,
  paymentMethods,
  onPaymentMethodChange,
}: Props) {
  const accountController = getModule(AccountController);
  const checkoutController = getModule(CheckoutController);

  const isSandbox = accountController.getSandbox();
  const { t } = useTranslation('account');
  const announce = useAriaAnnouncer();
  const navigate = useNavigate();
  const location = useLocation();
  const [session, setSession] = useState<AdyenPaymentSession>();

  const redirectResult = useQueryParam('redirectResult');
  const finalize = !!redirectResult;
  const paymentSuccessUrl = modalURLFromLocation(location, 'payment-method-success');

  const finalizePaymentDetails = useEventCallback(async (redirectResult: string) => {
    if (!selectedPaymentMethod) return;

    try {
      setProcessing(true);

      await checkoutController.finalizeAdyenPaymentDetails({ redirectResult: decodeURI(redirectResult) }, selectedPaymentMethod?.id);
      await accountController.reloadSubscriptions({ retry: 10 });

      setProcessing(false);

      announce(t('checkout.payment_success'), 'success');
      navigate(paymentSuccessUrl);
    } catch (error: unknown) {
      setProcessing(false);

      if (error instanceof Error) {
        setPaymentError(error.message);
        navigate(modalURLFromLocation(location, 'payment-method'), { replace: true });
      }
    }
  });

  useEffect(() => {
    if (!redirectResult) return;

    finalizePaymentDetails(redirectResult);
  }, [finalizePaymentDetails, redirectResult]);

  useEffect(() => {
    if (finalize) return;

    const createSession = async () => {
      setProcessing(true);

      try {
        const session = await checkoutController.createAdyenPaymentSession(window.origin, false);

        setSession(session);
      } catch (error: unknown) {
        if (error instanceof Error) {
          setPaymentError(error.message);
        }
      }

      setProcessing(false);
    };

    createSession();
  }, [setProcessing, setPaymentError, finalize, navigate, location, checkoutController]);

  const onSubmit = useEventCallback(async (state: AdyenEventData, handleAction: DropinElement['handleAction']) => {
    if (!state.isValid || !selectedPaymentMethod) return;

    try {
      setProcessing(true);
      setPaymentError(undefined);

      const returnUrl = modalURLFromWindowLocation('payment-method', { paymentMethodId: `${selectedPaymentMethod.id}` });
      const result = await checkoutController.addAdyenPaymentDetails(state.data.paymentMethod, selectedPaymentMethod.id, returnUrl);

      if ('action' in result) {
        handleAction(result.action);
      }

      await accountController.reloadSubscriptions({ retry: 5 });

      navigate(paymentSuccessUrl, { replace: true });
    } catch (error: unknown) {
      if (error instanceof Error) {
        setPaymentError(error.message);
      }
    }

    setProcessing(false);
  });

  const onAdditionalDetails = useEventCallback(async (state: AdyenAdditionalEventData) => {
    if (!selectedPaymentMethod) return;
    try {
      setProcessing(true);

      await checkoutController.finalizeAdyenPaymentDetails(state.data.details, selectedPaymentMethod.id);

      navigate(paymentSuccessUrl, { replace: true });
    } catch (error: unknown) {
      if (error instanceof Error) {
        setPaymentError(error.message);
        setProcessing(false);
      }
    }
  });

  const adyenConfiguration: CoreOptions = useMemo(
    () => ({
      showPayButton: false,
      environment: isSandbox ? 'test' : 'live',
      clientKey: session?.clientKey,
      paymentMethodsResponse: { paymentMethods: session?.paymentMethods || [] },
      paymentMethodsConfiguration: {
        card: {
          hasHolderName: true,
          holderNameRequired: true,
        },
      },
      onAdditionalDetails,
      onSubmit: (state: AdyenEventData, component: DropinElement) => onSubmit(state, component.handleAction),
      onError: (error: Error) => setPaymentError(error.message),
    }),
    [isSandbox, session?.clientKey, session?.paymentMethods, onAdditionalDetails, onSubmit, setPaymentError],
  );

  return (
    <Adyen
      configuration={adyenConfiguration}
      selectedPaymentMethod={selectedPaymentMethod}
      paymentMethods={paymentMethods}
      onPaymentMethodChange={onPaymentMethodChange}
      onPayPalSubmit={onPayPalSubmit}
      error={error}
    />
  );
}
